import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  H3,
  InputGroup,
  InputRightAddon,
  Spinner,
  TextInput,
} from '@ouster/webviz-react-components';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdOutlineStorage, MdOutlineWarningAmber } from 'react-icons/md';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { MergerStorageImpactResponse } from 'src/components/adminDashboard/types';
import { useStoreActions, useStoreState } from 'src/reducers';
import { isAdminLevelRole } from 'src/utils/roles';

import { StorageStats } from '../types';
import {
  buildStorageStatsFromImpact,
  computeInitialStorageOverride,
  convertBytesToDecimalGb,
  convertDecimalGbToBytes,
  formatBytesToDecimalGb,
} from '../utils';

interface StorageStepProps {
  survivorId: number;
  sourceIds: number[];
  initialNewStorageLimit: number;
  initialStorageImpactResponse?: MergerStorageImpactResponse | null;
  shouldUseInitialImpact?: boolean;
  isReadOnly?: boolean;
  onStorageStatsDraftChange: (stats: StorageStats) => void;
}

const DEFAULT_STORAGE_STATS: StorageStats = {
  newStorageLimit: 0,
  survivorUsedGb: 0,
  incomingGb: 0,
  totalRequiredGb: 0,
  basePlanLimitGb: 0,
  usedPercent: 0,
  isReadOnlyRisk: false,
};

const SUPPORT_STORAGE_CAP_MULTIPLIER = 1.2;

export default function StorageStep({
  survivorId,
  sourceIds,
  initialNewStorageLimit,
  initialStorageImpactResponse = null,
  shouldUseInitialImpact = false,
  isReadOnly = false,
  onStorageStatsDraftChange,
}: StorageStepProps) {
  const { curUserOrg } = useStoreState((storeState) => storeState.user);
  const { getStorageImpact } = useStoreActions(
    (actions) => actions.adminDashboard
  );
  const [impactResponse, setImpactResponse] =
    useState<MergerStorageImpactResponse | null>(null);
  const [storageOverrideDisplayGb, setStorageOverrideDisplayGb] =
    useState<number>(0);
  const [storageOverrideInput, setStorageOverrideInput] = useState<string>('0');
  const [isLoading, setIsLoading] = useState(false);
  const [isStorageOverrideFocused, setIsStorageOverrideFocused] =
    useState(false);
  const storageDebounceTimeoutRef = useRef<number | null>(null);

  const isSupportUser = curUserOrg?.role === 'SUPPORT';
  const hasUnlimitedOverride = isAdminLevelRole(curUserOrg?.role);

  const newStorageLimit = useMemo(
    () => convertDecimalGbToBytes(storageOverrideDisplayGb),
    [storageOverrideDisplayGb]
  );

  const storageStats = useMemo<StorageStats>(() => {
    if (impactResponse) {
      return buildStorageStatsFromImpact(impactResponse, newStorageLimit);
    }
    return DEFAULT_STORAGE_STATS;
  }, [impactResponse, newStorageLimit]);

  const supportMaxStorageLimit = useMemo(() => {
    if (!isSupportUser || !impactResponse) {
      return null;
    }

    const supportBaseLimit = Math.max(
      storageStats.basePlanLimitGb,
      storageStats.totalRequiredGb
    );

    return Math.ceil(supportBaseLimit * SUPPORT_STORAGE_CAP_MULTIPLIER);
  }, [
    impactResponse,
    isSupportUser,
    storageStats.basePlanLimitGb,
    storageStats.totalRequiredGb,
  ]);

  const supportMaxStorageDisplayGb = useMemo(
    () =>
      supportMaxStorageLimit === null
        ? null
        : convertBytesToDecimalGb(supportMaxStorageLimit),
    [supportMaxStorageLimit]
  );

  const clampOverrideGb = useCallback(
    (enteredGb: number): number => {
      const normalizedGb = Math.max(enteredGb, 0);

      if (hasUnlimitedOverride || supportMaxStorageDisplayGb === null) {
        return normalizedGb;
      }

      return Math.min(normalizedGb, supportMaxStorageDisplayGb);
    },
    [hasUnlimitedOverride, supportMaxStorageDisplayGb]
  );

  const resolveInitialLimitBytes = useCallback(
    (computedInitialLimit: number): number => {
      if (initialNewStorageLimit <= 0) {
        return computedInitialLimit;
      }

      // Never initialize below the topology-derived baseline.
      return Math.max(initialNewStorageLimit, computedInitialLimit);
    },
    [initialNewStorageLimit]
  );

  useEffect(() => {
    if (shouldUseInitialImpact && initialStorageImpactResponse) {
      // One-time initialization from initialStorageImpactResponse, an async
      // prop value seeded from mergerDetails (edit/view mode) instead of a
      // fresh fetch. impactResponse then becomes editable local state (the
      // user adjusts the storage override), so it can't be derived purely
      // from render.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setImpactResponse(initialStorageImpactResponse);
      const resolvedInitialLimit = resolveInitialLimitBytes(
        computeInitialStorageOverride(initialStorageImpactResponse)
      );
      const nextDisplayGb = clampOverrideGb(
        convertBytesToDecimalGb(resolvedInitialLimit)
      );
      setStorageOverrideDisplayGb(nextDisplayGb);
      setStorageOverrideInput(String(nextDisplayGb));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getStorageImpact({
      target_org: survivorId,
      source_orgs: sourceIds.join(','),
    })
      .then((response: MergerStorageImpactResponse) => {
        setImpactResponse(response);
        const resolvedInitialLimit = resolveInitialLimitBytes(
          computeInitialStorageOverride(response)
        );
        const nextDisplayGb = clampOverrideGb(
          convertBytesToDecimalGb(resolvedInitialLimit)
        );
        setStorageOverrideDisplayGb(nextDisplayGb);
        setStorageOverrideInput(String(nextDisplayGb));
      })
      .catch(() => {
        setImpactResponse(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    getStorageImpact,
    initialStorageImpactResponse,
    initialNewStorageLimit,
    shouldUseInitialImpact,
    sourceIds,
    survivorId,
    clampOverrideGb,
    resolveInitialLimitBytes,
  ]);

  useEffect(() => {
    if (!isStorageOverrideFocused) {
      return () => {};
    }

    if (storageDebounceTimeoutRef.current !== null) {
      window.clearTimeout(storageDebounceTimeoutRef.current);
    }

    storageDebounceTimeoutRef.current = window.setTimeout(() => {
      const trimmedValue = storageOverrideInput.trim();

      if (trimmedValue) {
        const enteredGb = Number(trimmedValue);
        if (!Number.isNaN(enteredGb)) {
          setStorageOverrideDisplayGb(clampOverrideGb(enteredGb));
        }
      }
    }, 300);

    return () => {
      if (storageDebounceTimeoutRef.current !== null) {
        window.clearTimeout(storageDebounceTimeoutRef.current);
        storageDebounceTimeoutRef.current = null;
      }
    };
  }, [clampOverrideGb, isStorageOverrideFocused, storageOverrideInput]);

  useEffect(() => {
    onStorageStatsDraftChange(storageStats);
  }, [onStorageStatsDraftChange, storageStats]);

  const capacityGb = Math.max(Number(storageStats.newStorageLimit ?? 0), 1);
  const totalRequiredGb = Math.max(storageStats.totalRequiredGb, 0);
  const filledPercent = Math.min((totalRequiredGb / capacityGb) * 100, 100);
  const survivorShare =
    totalRequiredGb > 0 ? storageStats.survivorUsedGb / totalRequiredGb : 0;
  const incomingShare =
    totalRequiredGb > 0 ? storageStats.incomingGb / totalRequiredGb : 0;
  const survivorPercent = Math.max(
    Math.min(filledPercent * survivorShare, filledPercent),
    0
  );
  const incomingPercent = Math.max(
    Math.min(filledPercent * incomingShare, filledPercent - survivorPercent),
    0
  );
  const remainingPercent = Math.max(100 - filledPercent, 0);
  const roundedUsage = Math.min(
    Math.max(Math.round(storageStats.usedPercent), 0),
    100
  );
  const shouldShowReadOnlyWarning = storageStats.isReadOnlyRisk;
  const chartData = [
    {
      name: 'Survivor Used',
      value: survivorPercent,
      color: '#6C63FF',
    },
    {
      name: 'Incoming Data',
      value: incomingPercent,
      color: '#F6AD1B',
    },
    {
      name: 'Remaining Capacity',
      value: remainingPercent,
      color: 'rgba(255, 255, 255, 0.18)',
    },
  ];

  return (
    <Box
      data-testid="merge-step-storage"
      display="grid"
      rowGap={5}
      overflow="auto"
      h="50vh"
      pr={6}
    >
      <Box>
        <Flex alignItems="center" justifyContent="space-between" gap={3} mb={2}>
          <Flex alignItems="center" gap={2}>
            <Box color="blue.300" display="flex" alignItems="center">
              <MdOutlineStorage size={20} />
            </Box>
            <H3 fontSize={26}>Storage Impact</H3>
          </Flex>
          {shouldShowReadOnlyWarning ? (
            <Flex
              color="red.300"
              fontSize={14}
              fontWeight={700}
              letterSpacing="0.04em"
              alignItems="center"
              gap={2}
              textTransform="uppercase"
              data-testid="merge-storage-risk-label"
            >
              <MdOutlineWarningAmber size={16} />
              Read-Only Risk
            </Flex>
          ) : null}
        </Flex>
        <Box color="gray.300" fontSize={14} mb={4}>
          Review post-merge capacity and optionally set a temporary storage
          override.
        </Box>
      </Box>

      <Box display="grid" rowGap={6} alignContent="start">
        {isLoading ? (
          <Flex
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius={4}
            minH="120px"
            alignItems="center"
            justifyContent="center"
            gap={3}
            color="gray.300"
            data-testid="merge-storage-loading"
          >
            <Spinner size="sm" />
            <Box fontSize={14}>Loading storage impact...</Box>
          </Flex>
        ) : null}

        {!isLoading ? (
          <>
            <Box w="100%" maxW="640px" mx="auto" h="120px">
              <ResponsiveContainer width="100%" height="100%">
                {/* 1. Remove default margins */}
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    startAngle={180}
                    endAngle={0}
                    cx="50%"
                    cy="100%" /* 2. Move center to the bottom */
                    innerRadius="140%" /* 3. Increase radii to fill the wider width */
                    outerRadius="195%"
                    stroke="none"
                    isAnimationActive
                    animationBegin={0}
                    animationDuration={700}
                    animationEasing="ease-out"
                  >
                    {chartData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Flex
              pointerEvents="none"
              alignItems="center"
              flexDirection="column"
              mt="-60px"
            >
              <Box
                fontSize={36}
                lineHeight="1"
                fontWeight={700}
                color={storageStats.isReadOnlyRisk ? 'red.300' : 'gray.100'}
                data-testid="merge-storage-usage-percent"
              >
                {roundedUsage}%
              </Box>
              <Box
                color="gray.400"
                fontSize={18}
                letterSpacing="-0.04em"
                textTransform="uppercase"
              >
                Capacity
              </Box>
            </Flex>

            <Box display="grid" rowGap={3} fontSize={14}>
              <Flex justifyContent="space-between" alignItems="center">
                <Flex alignItems="center" gap={2}>
                  <Box w={3} h={3} borderRadius="full" bg="blue.300" />
                  <Box color="gray.300" fontWeight={500}>
                    Survivor Used
                  </Box>
                </Flex>
                <Box fontWeight={700}>
                  {`${formatBytesToDecimalGb(storageStats.survivorUsedGb)} GB`}
                </Box>
              </Flex>
              <Flex justifyContent="space-between" alignItems="center">
                <Flex alignItems="center" gap={2}>
                  <Box w={3} h={3} borderRadius="full" bg="yellow.300" />
                  <Box color="gray.300" fontWeight={500}>
                    Incoming Data
                  </Box>
                </Flex>
                <Box color="yellow.300" fontWeight={700}>
                  +{`${formatBytesToDecimalGb(storageStats.incomingGb)} GB`}
                </Box>
              </Flex>
            </Box>

            <Box
              p={4}
              rounded={4}
              bg="gray.800"
              border="1px solid"
              borderColor="gray.600"
            >
              <FormControl paddingInline={0}>
                <FormLabel
                  textTransform="uppercase"
                  fontSize={12}
                  letterSpacing="0.05em"
                  color="gray.400"
                >
                  Storage Override
                </FormLabel>
                <InputGroup>
                  <TextInput
                    type="number"
                    min={0}
                    step={0.01}
                    isRequired
                    isDisabled={isReadOnly}
                    value={storageOverrideInput}
                    onFocus={() => setIsStorageOverrideFocused(true)}
                    onBlur={() => {
                      setIsStorageOverrideFocused(false);

                      if (storageDebounceTimeoutRef.current !== null) {
                        window.clearTimeout(storageDebounceTimeoutRef.current);
                        storageDebounceTimeoutRef.current = null;
                      }

                      const trimmedValue = storageOverrideInput.trim();
                      const enteredGb = trimmedValue ? Number(trimmedValue) : 0;
                      const sanitizedGb = Number.isNaN(enteredGb)
                        ? 0
                        : clampOverrideGb(enteredGb);

                      setStorageOverrideDisplayGb(sanitizedGb);
                      setStorageOverrideInput(String(sanitizedGb));
                    }}
                    onChange={(e) => {
                      setStorageOverrideInput(e.target.value);
                    }}
                    data-testid="merge-storage-override"
                  />
                  <InputRightAddon>GB</InputRightAddon>
                </InputGroup>
              </FormControl>

              {isSupportUser && supportMaxStorageDisplayGb !== null ? (
                <Box
                  mt={2}
                  fontSize={12}
                  color="yellow.300"
                  data-testid="merge-storage-support-max"
                >
                  {`Max override: ${formatBytesToDecimalGb(
                    supportMaxStorageLimit ?? 0
                  )} GB (120% of max plan limit or merged usage). Contact admin to increase further if needed.`}
                </Box>
              ) : null}

              <Flex
                justifyContent="space-between"
                mt={3}
                fontSize={13}
                color="gray.400"
              >
                <Box>
                  Original Plan Limit:{' '}
                  {`${formatBytesToDecimalGb(storageStats.basePlanLimitGb)} GB`}
                </Box>
                <Box>
                  Total Req:{' '}
                  {`${formatBytesToDecimalGb(storageStats.totalRequiredGb)} GB`}
                </Box>
              </Flex>
            </Box>

            {shouldShowReadOnlyWarning ? (
              <Box
                p={3}
                border="1px solid"
                borderColor="red.500"
                bg="rgba(105, 36, 36, 0.35)"
                borderRadius={6}
                color="red.200"
                data-testid="merge-storage-warning"
              >
                <Flex alignItems="flex-start" gap={2}>
                  <Box mt="2px">
                    <MdOutlineWarningAmber size={18} />
                  </Box>
                  <Box>
                    <Box fontWeight={700} mb={2}>
                      Warning: Read-Only Mode
                    </Box>
                    <Box>
                      Post-merge organization will be locked to Read-Only access
                      unless storage limit is increased.
                    </Box>
                  </Box>
                </Flex>
              </Box>
            ) : null}
          </>
        ) : null}
      </Box>
    </Box>
  );
}
